import { Button as MuiButton, type ButtonProps } from '@mui/material'
import clsx from 'clsx'
import React from 'react'

import { CircularLoading } from '../../feedback/deprecated/circular-loading/CircularLoading'

import { useAppButtonStyles } from './AppButton.styles'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'secondary-dark-bg'
  | 'delete'
  | 'delete-inverted'
  | 'text'

type ExtraProps = {
  variant?: ButtonVariant
  size?: 'default' | 'small'
  isLoading?: boolean
}

type AllowedMuiButtonProps = Omit<
  // we are forcing the component type as it doesn't seem to be possible
  // to do this generically with mui@v4 and overriding the colors
  // see https://github.com/mui-org/material-ui/issues/15827#issuecomment-1010300346
  ButtonProps<'button'>,
  'color' | 'size' | 'variant'
>

export type AppButtonProps = AllowedMuiButtonProps & ExtraProps

// TODO(MUI5): remove this component because there is a LoadingButton in v5 which will allow us to customize default button via theme
export const AppButton = React.forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'default',
      isLoading,
      className,
      fullWidth,
      classes,
      href,
      ...rest
    },
    ref,
  ) => {
    const { cx, classes: tssClasses } = useAppButtonStyles()

    const finalVariant: ButtonProps['variant'] = (
      {
        primary: 'contained',
        secondary: 'outlined',
        'secondary-dark-bg': 'outlined',
        delete: 'contained',
        'delete-inverted': 'contained',
        text: 'text',
      } as const
    )[variant]

    const buttonClassNames = cx(
      {
        primary: [
          tssClasses.button,
          tssClasses.primary,
          tssClasses.containedDisabled,
        ],
        secondary: [
          tssClasses.button,
          tssClasses.secondary,
          tssClasses.secondaryDisabled,
        ],
        'secondary-dark-bg': [
          tssClasses.button,
          tssClasses.secondaryDarkBg,
          tssClasses.secondaryDarkBgDisabled,
        ],
        delete: [
          tssClasses.button,
          tssClasses.delete,
          tssClasses.containedDisabled,
        ],
        'delete-inverted': [
          tssClasses.button,
          tssClasses.deleteInverted,
          tssClasses.containedDisabled,
        ],
        text: '',
      }[variant],
      { [tssClasses.disableEvents]: isLoading },
      tssClasses[`size_${size}`],
    )

    return (
      <MuiButton
        {...rest}
        href={href}
        startIcon={!isLoading ? rest.startIcon : undefined}
        endIcon={!isLoading ? rest.endIcon : undefined}
        ref={ref}
        variant={finalVariant}
        classes={{
          ...classes,
          root: clsx(buttonClassNames, classes?.root),
        }}
        className={clsx(!isLoading && className)}
        fullWidth={fullWidth}
      >
        {isLoading ? (
          <div className={tssClasses.transparent}>{children}</div>
        ) : (
          children
        )}
        {isLoading && (
          <CircularLoading
            size="small"
            color={variant}
            className={tssClasses.loader}
          />
        )}
      </MuiButton>
    )
  },
)
